import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/errors/use-toast';
import { useHeaderGeneration } from '@/hooks/text-generation';
import { researchClient } from '@/integrations/clients';
import { IntegrationError } from '@/integrations/shared';
import type { ResearchRequest } from '@/integrations/perplexity';
import { RelatedQuestionGenerationOptions } from './types';

export const useRelatedQuestionGeneration = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { generateHeader } = useHeaderGeneration();
  const queryClient = useQueryClient();

  const generateResearchFromQuestion = async (options: RelatedQuestionGenerationOptions): Promise<string | null> => {
    setIsGenerating(true);
    setError(null);

    try {
      console.log('[useRelatedQuestionGeneration] Starting research generation for question:', options.question);

      // Step 1: Build research request with original article context
      // Get the original research title to provide context for the related question
      const { data: { user: currentUser }, error: userAuthError } = await supabase.auth.getUser();
      if (userAuthError || !currentUser) {
        throw new Error('User not authenticated');
      }

      // Fetch the original research to get its title for context
      let originalResearchTitle = '';
      if (options.currentResearchId) {
        const { data: originalResearch, error: fetchError } = await supabase
          .from('research_library')
          .select('title')
          .eq('id', options.currentResearchId)
          .eq('user_id', currentUser.id)
          .single();

        if (!fetchError && originalResearch) {
          originalResearchTitle = originalResearch.title;
        }
      }

      // Build enhanced prompt with original article context
      const enhancedPrompt = originalResearchTitle 
        ? `${options.question}. Relate your response to "${originalResearchTitle}"`
        : options.question;

      const researchRequest: ResearchRequest = {
        researchPrompt: enhancedPrompt,
        researchType: options.researchParameters.research_type || 'comprehensive_analysis',
        timeframe: options.researchParameters.timeframe,
        country: options.researchParameters.country,
        sourceType: options.researchParameters.source_type,
        selectedFilters: options.researchParameters.type_specific_filters || {}
      };

      // Call the research client instead of direct edge function
      let researchContent;
      try {
        researchContent = await researchClient.generateResearch(researchRequest);
      } catch (clientError) {
        console.error('[useRelatedQuestionGeneration] Research client error:', clientError);
        const errorMessage = clientError instanceof IntegrationError 
          ? clientError.message 
          : (clientError instanceof Error ? clientError.message : 'Failed to generate research');
        setError(errorMessage);
        toast({
          title: 'Research generation failed',
          description: errorMessage,
          variant: 'destructive',
        });
        return null;
      }

      if (!researchContent || !researchContent.content) {
        console.error('[useRelatedQuestionGeneration] No research content returned:', researchContent);
        setError('No research content generated');
        toast({
          title: 'Research generation failed',
          description: 'No research content was generated from the question',
          variant: 'destructive',
        });
        return null;
      }

      console.log('[useRelatedQuestionGeneration] Research content generated successfully');

      // Step 2: Generate a header for the research (following useResearchGeneration pattern)
      let title: string;
      try {
        console.log('[useRelatedQuestionGeneration] Generating header for research content...');
        const headerResult = await generateHeader({
          content: researchContent.content || '',
          originalPrompt: options.question,
          context: `Research Type: ${options.researchParameters.research_type || 'comprehensive_analysis'}${options.researchParameters.timeframe ? `, Timeframe: ${options.researchParameters.timeframe}` : ''}${options.researchParameters.country ? `, Country: ${options.researchParameters.country}` : ''}`,
        });

        title = headerResult.header;
        console.log('[useRelatedQuestionGeneration] Generated header:', title);
      } catch (headerError) {
        console.error('[useRelatedQuestionGeneration] Header generation failed, using fallback:', headerError);
        // Fallback to original question if header generation fails
        title = options.question.substring(0, 100);
      }

      // Step 3: Get authenticated user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Step 4: Build research parameters (following useResearchGeneration pattern)
      const effectiveResearchType = options.researchParameters.research_type || 'comprehensive_analysis';
      const researchParameters = {
        research_type: effectiveResearchType,
        timeframe: options.researchParameters.timeframe || null,
        timeframe_label: researchContent.timeframeLabel || null,
        date_range_start: researchContent.dateRangeStart || null,
        date_range_end: researchContent.dateRangeEnd || null,
        country: options.researchParameters.country || null,
        source_type: options.researchParameters.source_type || null,
        type_specific_filters: options.researchParameters.type_specific_filters || {}
      };

      console.log('[useRelatedQuestionGeneration] Research parameters object:', researchParameters);

      // Step 5: Save the research to the database (following useResearchGeneration pattern)
      const { data: savedResearch, error: saveError } = await supabase
        .from('research_library')
        .insert({
          user_id: user.id,
          title,
          content: researchContent.content,
          research_type: effectiveResearchType,
          original_prompt: options.question,
          metadata: researchContent.metadata || {},
          sources: researchContent.sources || [],
          related_questions: researchContent.related_questions || [],
          usage: researchContent.usage || {},
          research_parameters: researchParameters,
          system_prompt: researchContent.system_prompt || null,
        })
        .select()
        .single();

      if (saveError) {
        console.error('[useRelatedQuestionGeneration] Error saving research:', saveError);
        setError(`Failed to save research: ${saveError.message}`);
        toast({
          title: 'Research save failed',
          description: `Failed to save research: ${saveError.message}`,
          variant: 'destructive',
        });
        return null;
      }

      console.log('[useRelatedQuestionGeneration] Research saved successfully with ID:', savedResearch.id);
      
      toast({
        title: 'Research generated',
        description: 'New research has been generated from your question',
      });

      // Invalidate research library queries to update the sidebar (following useResearchGeneration pattern)
      console.log('[useRelatedQuestionGeneration] Research generation successful, invalidating list queries');
      queryClient.invalidateQueries({ 
        queryKey: ['research-library-list'],
        exact: false,
        refetchType: 'active'
      });
      
      // Also invalidate the specific research item details to update the sidebar
      if (options.currentResearchId) {
        queryClient.invalidateQueries({
          queryKey: ['research-item-details', options.currentResearchId]
        });
        console.log('[useRelatedQuestionGeneration] Invalidated research item details for:', options.currentResearchId);
      }
      
      console.log('[useRelatedQuestionGeneration] Query invalidation completed');

      return savedResearch.id;
    } catch (err: any) {
      console.error('[useRelatedQuestionGeneration] Error generating research:', err);
      const errorMessage = err.message || 'An unexpected error occurred';
      setError(errorMessage);
      
      toast({
        title: 'Research generation failed',
        description: errorMessage,
        variant: 'destructive',
      });
      
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    generateResearchFromQuestion,
    isGenerating,
    error,
    clearError: () => setError(null)
  };
};

